/**
 * Click-to-copy chip showing a shortened UUID. Primarily used on the Feedback
 * detail page so the user (or the /moftaki-fix skill caller) can grab the
 * FeedbackTicketKey as an unambiguous identifier across dev / prod.
 */
import { useState } from 'react';
import { Chip, Tooltip } from '@mui/material';
import { ContentCopy, Check } from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';

interface Props {
  value: string;
  label?: string;
}

export const CopyableKey = ({ value, label }: Props) => {
  const theme = useTheme();
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Fallback for older browsers / non-secure contexts
      const input = document.createElement('input');
      input.value = value;
      document.body.appendChild(input);
      input.select();
      try { document.execCommand('copy'); setCopied(true); setTimeout(() => setCopied(false), 1500); }
      finally { document.body.removeChild(input); }
    }
  };

  const display = label || (value.length > 12 ? `${value.slice(0, 8)}…${value.slice(-4)}` : value);

  return (
    <Tooltip title={copied ? 'Copied!' : `Click to copy: ${value}`} placement="top">
      <Chip
        size="small"
        icon={copied ? <Check sx={{ fontSize: 12 }} /> : <ContentCopy sx={{ fontSize: 12 }} />}
        label={display}
        onClick={handleCopy}
        sx={{
          fontFamily: 'monospace', fontSize: '0.7rem', height: 22,
          bgcolor: copied ? alpha(theme.palette.success.main, 0.12) : alpha(theme.palette.info.main, 0.08),
          color: copied ? 'success.main' : 'text.secondary',
          cursor: 'pointer',
          '& .MuiChip-icon': { color: 'inherit', ml: 0.5, mr: -0.3 },
          '&:hover': {
            bgcolor: copied ? alpha(theme.palette.success.main, 0.18) : alpha(theme.palette.info.main, 0.14),
          },
        }}
      />
    </Tooltip>
  );
};
